// src/components/background/StarryBackground.js
import React, { useRef, useMemo, useContext } from 'react';
import * as THREE from 'three';
import { useFrame, Canvas } from '@react-three/fiber';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import styled from '@emotion/styled';
import { ThemeContext } from '../../context/ThemeContext';
import { colors } from '../../theme';

// Conteneur fixé qui prend tout l'écran, placé en arrière-plan
const BackgroundContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: -1;
  background: ${props => props.isDark 
    ? `linear-gradient(135deg, rgba(15, 28, 63, 0.4) 0%, rgba(18, 18, 18, 0.35) 100%)` 
    : `linear-gradient(135deg, rgba(30, 108, 187, 0.4) 0%, rgba(10, 53, 107, 0.35) 100%)`
  };
  overflow: hidden;
`;

// Étoile améliorée avec effet de scintillement
const Star = ({ position, size = 0.05, color = '#ffffff', blinking = false }) => {
  const ref = useRef();
  const glowRef = useRef();
  const randomOffset = useMemo(() => Math.random() * 1000, []);
  
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() + randomOffset;
    
    if (blinking) {
      // Effet de scintillement plus naturel
      const blink = Math.sin(t * 1.5) * 0.5 + 1.5;
      ref.current.scale.set(blink, blink, blink);
      
      // Faire varier légèrement la luminosité
      const opacity = Math.sin(t * 2) * 0.3 + 0.7;
      ref.current.material.opacity = opacity;
      
      if (glowRef.current) {
        glowRef.current.scale.set(blink * 2, blink * 2, blink * 2);
        glowRef.current.material.opacity = opacity * 0.5;
      }
    }
  });
  
  return (
    <group>
      <mesh ref={ref} position={position}>
        <sphereGeometry args={[size, 16, 16]} />
        <meshBasicMaterial color={color} transparent />
      </mesh>
      <mesh ref={glowRef} position={position}>
        <sphereGeometry args={[size * 2, 16, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.3} />
      </mesh>
    </group>
  );
};

// Étoile filante avec traînée plus visible
const ShootingStar = ({ startPosition, direction, speed = 0.1, color = '#ffffff', size = 0.08 }) => {
  const ref = useRef();
  const tailRef = useRef();
  const glowRef = useRef();
  const initialPosition = useMemo(() => [...startPosition], [startPosition]);
  
  useFrame(() => {
    // Déplacer l'étoile dans la direction
    ref.current.position.x += direction[0] * speed;
    ref.current.position.y += direction[1] * speed;
    ref.current.position.z += direction[2] * speed;
    
    // Déplacer la traînée et la lueur avec l'étoile
    tailRef.current.position.copy(ref.current.position);
    glowRef.current.position.copy(ref.current.position);
    
    // Orienter la traînée dans la direction opposée au mouvement
    const angle = Math.atan2(-direction[1], -direction[0]);
    tailRef.current.rotation.z = angle;
    
    // Vérifier si l'étoile est sortie de l'écran
    const distanceTraveled = Math.sqrt(
      Math.pow(ref.current.position.x - initialPosition[0], 2) +
      Math.pow(ref.current.position.y - initialPosition[1], 2)
    );
    
    if (distanceTraveled > 40) {
      // Réinitialiser l'étoile à une nouvelle position aléatoire
      const side = Math.floor(Math.random() * 4); // 0: haut, 1: droite, 2: bas, 3: gauche
      let x, y;
      
      switch (side) {
        case 0: // haut
          x = (Math.random() - 0.5) * 70;
          y = 40;
          break;
        case 1: // droite
          x = 40;
          y = (Math.random() - 0.5) * 70;
          break;
        case 2: // bas
          x = (Math.random() - 0.5) * 70;
          y = -40;
          break;
        case 3: // gauche
        default:
          x = -40;
          y = (Math.random() - 0.5) * 70;
          break;
      }
      
      ref.current.position.set(x, y, initialPosition[2]);
    }
  });
  
  return (
    <group>
      {/* Étoile principale */}
      <mesh ref={ref} position={startPosition}>
        <sphereGeometry args={[size, 16, 16]} />
        <meshBasicMaterial color={color} />
      </mesh>
      
      {/* Lueur autour de l'étoile */}
      <mesh ref={glowRef} position={startPosition}>
        <sphereGeometry args={[size * 2, 16, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.4} />
      </mesh>
      
      {/* Traînée luminescente plus longue et plus visible */}
      <mesh ref={tailRef} position={startPosition}>
        <coneGeometry args={[size/1.5, size*20, 16, 1]} rotation={[0, 0, Math.PI/2]} />
        <meshBasicMaterial color={color} transparent opacity={0.7} />
      </mesh>
    </group>
  );
};

// Structure qui se déplace lentement à travers l'écran
const FloatingBuild = ({ position, size = 0.2, color = '#ffffff', speed = 0.01 }) => {
  const ref = useRef();
  const glowRef = useRef();
  const randomOffset = useMemo(() => Math.random() * 1000, []);
  const direction = useMemo(() => {
    const angle = Math.random() * Math.PI * 2;
    return [Math.cos(angle) * speed, Math.sin(angle) * speed, 0];
  }, [speed]);
  
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() + randomOffset;
    
    // Mouvement lent et organique
    ref.current.position.x += direction[0];
    ref.current.position.y += direction[1];
    
    // Pulsation légère
    const pulse = Math.sin(t * 0.3) * 0.2 + 1.0;
    ref.current.scale.set(pulse, pulse, pulse);
    glowRef.current.scale.set(pulse * 1.5, pulse * 1.5, pulse * 1.5);
    
    // Faire rebondir sur les bords
    if (Math.abs(ref.current.position.x) > 45) {
      direction[0] = -direction[0];
    }
    if (Math.abs(ref.current.position.y) > 45) {
      direction[1] = -direction[1];
    }
    
    // Faire osciller en profondeur
    ref.current.position.z = Math.sin(t * 0.2) * 5;
    glowRef.current.position.copy(ref.current.position);
  });
  
  // Forme plus complexe pour ressembler à un "build"
  return (
    <group>
      <mesh ref={ref} position={position}>
        <octahedronGeometry args={[size, 2]} />
        <meshBasicMaterial color={color} transparent opacity={0.8} />
      </mesh>
      <mesh ref={glowRef} position={position}>
        <sphereGeometry args={[size * 1.5, 16, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.3} />
      </mesh>
    </group>
  );
};

// Nouveau composant pour les neurones
const Neuron = ({ position, size = 0.15, color = '#00ffff', speed = 0.02 }) => {
  const coreRef = useRef();
  const glowRef = useRef();
  const synapsesRef = useRef();
  const randomOffset = useMemo(() => Math.random() * 1000, []);
  
  // Créer une direction aléatoire de mouvement
  const direction = useMemo(() => {
    const angle = Math.random() * Math.PI * 2;
    return [Math.cos(angle) * speed, Math.sin(angle) * speed, 0];
  }, [speed]);
  
  // Créer des dendrites (branches)
  const dendrites = useMemo(() => {
    const count = Math.floor(Math.random() * 3) + 3; // 3-5 dendrites
    const result = [];
    
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const length = Math.random() * 0.7 + 0.3;
      
      result.push({
        direction: [Math.cos(angle), Math.sin(angle), 0],
        length,
        width: Math.random() * 0.03 + 0.01
      });
    }
    
    return result;
  }, []);
  
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() + randomOffset;
    
    // Mouvement ondulatoire pour simuler un déplacement sur une "plage"
    // On utilise un mouvement horizontal plus prononcé
    const beachY = -25; // Position Y de la "plage"
    const amplitude = 2;
    const frequency = 0.3;
    
    // Déplacement horizontal (le long de la plage)
    coreRef.current.position.x += direction[0];
    
    // Déplacement légèrement vertical pour simuler le mouvement sur la plage
    const verticalOffset = Math.sin(t * frequency + coreRef.current.position.x * 0.1) * amplitude * 0.3;
    coreRef.current.position.y = beachY + verticalOffset;
    
    // Créer un effet de "marche" en oscillant la hauteur z
    coreRef.current.position.z = Math.sin(t * 2) * 0.2;
    
    // Faire rebondir sur les bords horizontaux
    if (Math.abs(coreRef.current.position.x) > 45) {
      direction[0] = -direction[0];
    }
    
    // Pulsation pour simuler l'activité neuronale
    const pulse = Math.sin(t * 1.5) * 0.15 + 1.0;
    coreRef.current.scale.set(pulse, pulse, pulse);
    
    // Synchroniser les positions des autres éléments
    glowRef.current.position.copy(coreRef.current.position);
    glowRef.current.scale.set(pulse * 1.5, pulse * 1.5, pulse * 1.5);
    synapsesRef.current.position.copy(coreRef.current.position);
    
    // Faire scintiller les synapses
    const synapseOpacity = Math.sin(t * 2) * 0.3 + 0.7;
    synapsesRef.current.children.forEach((synapse, i) => {
      const pulseOffset = Math.sin(t * 1 + i) * 0.2 + 0.8;
      synapse.scale.set(pulseOffset, pulseOffset, 1);
      synapse.material.opacity = synapseOpacity * 0.6;
    });
  });
  
  return (
    <group>
      {/* Corps du neurone */}
      <mesh ref={coreRef} position={position}>
        <sphereGeometry args={[size, 16, 16]} />
        <meshBasicMaterial color={color} />
      </mesh>
      
      {/* Lueur du neurone */}
      <mesh ref={glowRef} position={position}>
        <sphereGeometry args={[size * 2, 16, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.4} />
      </mesh>
      
      {/* Dendrites / Synapses */}
      <group ref={synapsesRef} position={position}>
        {dendrites.map((dendrite, i) => (
          <mesh key={i} position={[
            dendrite.direction[0] * dendrite.length * size * 5,
            dendrite.direction[1] * dendrite.length * size * 5,
            dendrite.direction[2] * dendrite.length * size * 5
          ]}>
            <sphereGeometry args={[size * 0.4, 8, 8]} />
            <meshBasicMaterial color={color} transparent opacity={0.6} />
          </mesh>
        ))}
      </group>
    </group>
  );
};

// Ajout d'un nouveau composant pour les connexions entre neurones
const NeuralConnection = ({ startNeuron, endNeuron, color = '#00ffff', pulseSpeed = 0.5 }) => {
  const lineRef = useRef();
  const particlesRef = useRef();
  const startPos = useRef(new THREE.Vector3(...startNeuron));
  const endPos = useRef(new THREE.Vector3(...endNeuron));
  
  // Créer des particules qui se déplaceront le long de la connexion
  const particles = useMemo(() => {
    const count = Math.floor(Math.random() * 3) + 2; // 2-4 particules
    const result = [];
    
    for (let i = 0; i < count; i++) {
      result.push({
        progress: Math.random(), // Position initiale aléatoire sur la ligne
        speed: Math.random() * 0.003 + 0.001, // Vitesse variable
        size: Math.random() * 0.04 + 0.02 // Taille variable
      });
    }
    
    return result;
  }, []);
  
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    
    // Mettre à jour la géométrie de la ligne pour connecter les deux neurones
    const points = [startPos.current, endPos.current];
    lineRef.current.geometry.setFromPoints(points);
    
    // Faire pulser l'opacité de la ligne
    const opacity = Math.sin(t * pulseSpeed) * 0.3 + 0.5;
    lineRef.current.material.opacity = opacity;
    
    // Animer les particules le long de la connexion
    particles.forEach((particle, i) => {
      if (particlesRef.current.children[i]) {
        // Calculer la nouvelle position sur la ligne
        particle.progress += particle.speed;
        if (particle.progress > 1) particle.progress = 0;
        
        // Interpoler entre les deux points
        const x = startPos.current.x + (endPos.current.x - startPos.current.x) * particle.progress;
        const y = startPos.current.y + (endPos.current.y - startPos.current.y) * particle.progress;
        const z = startPos.current.z + (endPos.current.z - startPos.current.z) * particle.progress;
        
        particlesRef.current.children[i].position.set(x, y, z);
        
        // Faire pulser les particules
        const particlePulse = Math.sin(t * 2 + i) * 0.3 + 0.7;
        particlesRef.current.children[i].scale.set(particlePulse, particlePulse, particlePulse);
      }
    });
  });
  
  return (
    <group>
      {/* Ligne de connexion */}
      <line ref={lineRef}>
        <bufferGeometry />
        <lineBasicMaterial color={color} transparent opacity={0.5} />
      </line>
      
      {/* Particules se déplaçant le long de la connexion */}
      <group ref={particlesRef}>
        {particles.map((particle, i) => (
          <mesh key={i} position={[startPos.current.x, startPos.current.y, startPos.current.z]}>
            <sphereGeometry args={[particle.size, 8, 8]} />
            <meshBasicMaterial color={color} transparent opacity={0.8} />
          </mesh>
        ))}
      </group>
    </group>
  );
};

// Composant pour la "plage" où les neurones se déplacent
const NeuralBeach = ({ color = '#00aaff', position = [0, -25, 0], width = 100, depth = 20 }) => {
  const beachRef = useRef();
  const wavesRef = useRef();
  
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    
    // Animer les vagues sur la plage
    if (wavesRef.current) {
      const wavesGeometry = wavesRef.current.geometry;
      const positions = wavesGeometry.attributes.position;
      
      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const z = positions.getZ(i);
        
        // Créer un effet de vague
        const waveHeight = Math.sin(x * 0.2 + t * 0.7) * 0.2 + 
                         Math.sin(z * 0.3 + t * 0.5) * 0.3;
        
        positions.setY(i, waveHeight);
      }
      
      positions.needsUpdate = true;
    }
  });
  
  return (
    <group ref={beachRef} position={position}>
      {/* Surface de la plage avec un dégradé */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[width, depth, 32, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.2} />
      </mesh>
      
      {/* Vagues animées */}
      <mesh ref={wavesRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.5, 0]}>
        <planeGeometry args={[width, depth, 48, 12]} />
        <meshBasicMaterial 
          color={color} 
          transparent 
          opacity={0.15} 
          wireframe={true}
        />
      </mesh>
    </group>
  );
};

// Composant du ciel étoilé
const StarrySky = ({ isDarkMode }) => {
  const groupRef = useRef();
  
  // Couleurs depuis l'objet colors de theme
  const primaryColor = isDarkMode ? colors.skyBlue : colors.bluePrimary;
  const secondaryColor = isDarkMode ? colors.gold : colors.orangeAccent;
  const tertiaryColor = isDarkMode ? colors.greenSuccess : colors.greenSuccess;
  const starColor = colors.white;
  const neuronColor = isDarkMode ? '#00aaff' : '#0088ff';
  const neuronColor2 = isDarkMode ? '#00ffaa' : '#00dd88';
  
  // Rotation très lente de tout le ciel
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() * 0.03;
    groupRef.current.rotation.z = Math.sin(t * 0.3) * 0.02;
    groupRef.current.rotation.x = Math.cos(t * 0.2) * 0.01;
    groupRef.current.rotation.y = Math.sin(t * 0.1) * 0.01;
  });
  
  // Générer les étoiles
  const stars = useMemo(() => {
    const count = 500; // Beaucoup plus d'étoiles pour un ciel bien rempli
    const result = [];
    
    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 100; // Zone plus large
      const y = (Math.random() - 0.5) * 100;
      const z = (Math.random() - 0.5) * 50 - 10; // Principalement derrière
      
      const size = Math.random() * 0.12 + 0.04; // Tailles variées
      
      // Distribution des couleurs
      const color = i % 15 === 0 ? secondaryColor : 
                   i % 20 === 0 ? primaryColor : 
                   i % 30 === 0 ? tertiaryColor : 
                   starColor;
                   
      // Plus d'étoiles clignotent
      const blinking = i % 2 === 0;
      
      result.push({ position: [x, y, z], size, color, blinking });
    }
    
    return result;
  }, [primaryColor, secondaryColor, tertiaryColor, starColor]);
  
  // Générer des étoiles filantes
  const shootingStars = useMemo(() => {
    const count = 15; // Plus d'étoiles filantes pour un effet spectaculaire
    const result = [];
    
    for (let i = 0; i < count; i++) {
      // Position au bord de l'écran
      const side = Math.floor(Math.random() * 4); // 0: haut, 1: droite, 2: bas, 3: gauche
      let x, y, dirX, dirY;
      
      switch (side) {
        case 0: // haut
          x = (Math.random() - 0.5) * 80;
          y = 45;
          dirX = Math.random() * 0.06 - 0.03;
          dirY = -Math.random() * 0.06 - 0.03;
          break;
        case 1: // droite
          x = 45;
          y = (Math.random() - 0.5) * 80;
          dirX = -Math.random() * 0.06 - 0.03;
          dirY = Math.random() * 0.06 - 0.03;
          break;
        case 2: // bas
          x = (Math.random() - 0.5) * 80;
          y = -45;
          dirX = Math.random() * 0.06 - 0.03;
          dirY = Math.random() * 0.06 + 0.03;
          break;
        case 3: // gauche
        default:
          x = -45;
          y = (Math.random() - 0.5) * 80;
          dirX = Math.random() * 0.06 + 0.03;
          dirY = Math.random() * 0.06 - 0.03;
          break;
      }
      
      // Z derrière le réseau
      const z = -15;
      const speed = Math.random() * 0.15 + 0.08; // Vitesse variée
      
      // Couleurs variées pour les étoiles filantes
      const color = i % 3 === 0 ? secondaryColor : 
                   i % 5 === 0 ? tertiaryColor : 
                   primaryColor;
                   
      const size = Math.random() * 0.15 + 0.12; // Taille variée
      
      result.push({
        startPosition: [x, y, z],
        direction: [dirX, dirY, 0],
        speed,
        color,
        size
      });
    }
    
    return result;
  }, [primaryColor, secondaryColor, tertiaryColor]);
  
  // Générer des neurones flottants et quelques builds (moins qu'avant)
  const floatingElements = useMemo(() => {
    // On réduit le nombre total d'éléments flottants pour éviter la surcharge
    const neuronCount = 12; // Neurones flottants
    const buildCount = 8; // Quelques builds restants
    const result = [];
    
    // Créer des neurones flottants
    for (let i = 0; i < neuronCount; i++) {
      const x = (Math.random() - 0.5) * 80; 
      const y = (Math.random() - 0.5) * 70;
      const z = (Math.random() - 0.5) * 30;
      
      const size = Math.random() * 0.2 + 0.1; // Tailles variées pour les neurones
      
      // Couleurs spécifiques pour les neurones (plus cohérents avec ceux de la plage)
      const color = i % 2 === 0 ? neuronColor : neuronColor2;
      
      // Vitesse de déplacement variable
      const speed = Math.random() * 0.012 + 0.005;
      
      // Ajouter un attribut de type pour distinguer les neurones et les builds
      result.push({ 
        position: [x, y, z], 
        size, 
        color, 
        speed,
        type: "neuron" 
      });
    }
    
    // Ajouter quelques builds restants
    for (let i = 0; i < buildCount; i++) {
      const x = (Math.random() - 0.5) * 80; 
      const y = (Math.random() - 0.5) * 70;
      const z = (Math.random() - 0.5) * 30;
      
      const size = Math.random() * 0.3 + 0.15; // Tailles variées pour les builds
      
      // Distribution des couleurs pour les builds
      const color = i % 3 === 0 ? secondaryColor : 
                   i % 4 === 0 ? primaryColor : 
                   tertiaryColor;
      
      // Vitesse de déplacement variable
      const speed = Math.random() * 0.015 + 0.004;
      
      result.push({ 
        position: [x, y, z], 
        size, 
        color, 
        speed,
        type: "build" 
      });
    }
    
    return result;
  }, [primaryColor, secondaryColor, tertiaryColor, starColor, neuronColor, neuronColor2]);
  
  // Générer des neurones sur la plage
  const beachNeurons = useMemo(() => {
    const count = 15; // Nombre réduit de neurones sur la plage pour éviter la surcharge
    const result = [];
    
    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 80; // Position horizontale le long de la plage
      const y = -25 + (Math.random() - 0.5) * 5; // Position verticale sur la plage
      const z = (Math.random() - 0.5) * 10; // Légère variation en profondeur
      
      const size = Math.random() * 0.25 + 0.15; // Tailles variées
      
      // Variation de couleurs entre deux teintes
      const color = i % 2 === 0 ? neuronColor : neuronColor2;
      
      // Vitesse variable
      const speed = Math.random() * 0.03 + 0.01;
      
      result.push({ position: [x, y, z], size, color, speed });
    }
    
    return result;
  }, [neuronColor, neuronColor2]);
  
  // Générer des connexions entre neurones
  const neuralConnections = useMemo(() => {
    const connections = [];
    const connectionCount = 10; // Réduction du nombre de connexions pour plus de clarté
    
    // Sélectionner des neurones aléatoires à connecter
    for (let i = 0; i < connectionCount; i++) {
      const startIdx = Math.floor(Math.random() * beachNeurons.length);
      let endIdx;
      
      // S'assurer que le neurone de destination est différent
      do {
        endIdx = Math.floor(Math.random() * beachNeurons.length);
      } while (endIdx === startIdx);
      
      const startNeuron = beachNeurons[startIdx].position;
      const endNeuron = beachNeurons[endIdx].position;
      
      // Couleur basée sur les neurones connectés
      const color = i % 3 === 0 ? neuronColor2 : neuronColor;
      
      // Vitesse de pulsation variable
      const pulseSpeed = Math.random() * 1 + 0.3;
      
      connections.push({
        startNeuron,
        endNeuron,
        color,
        pulseSpeed
      });
    }
    
    return connections;
  }, [beachNeurons, neuronColor, neuronColor2]);
  
  return (
    <group ref={groupRef}>
      {/* Étoiles statiques et clignotantes */}
      {stars.map((star, i) => (
        <Star
          key={`star-${i}`}
          position={star.position}
          size={star.size}
          color={star.color}
          blinking={star.blinking}
        />
      ))}
      
      {/* Étoiles filantes */}
      {shootingStars.map((star, i) => (
        <ShootingStar
          key={`shooting-${i}`}
          startPosition={star.startPosition}
          direction={star.direction}
          speed={star.speed}
          color={star.color}
          size={star.size}
        />
      ))}
      
      {/* Éléments flottants (neurones et builds) */}
      {floatingElements.map((element, i) => (
        element.type === "neuron" ? (
          <Neuron
            key={`floating-neuron-${i}`}
            position={element.position}
            size={element.size}
            color={element.color}
            speed={element.speed}
          />
        ) : (
          <FloatingBuild
            key={`floating-build-${i}`}
            position={element.position}
            size={element.size}
            color={element.color}
            speed={element.speed}
          />
        )
      ))}
      
      {/* Plage neurale */}
      <NeuralBeach 
        color={neuronColor} 
        position={[0, -25, 0]} 
        width={100} 
        depth={20}
      />
      
      {/* Neurones se déplaçant sur la plage */}
      {beachNeurons.map((neuron, i) => (
        <Neuron
          key={`neuron-${i}`}
          position={neuron.position}
          size={neuron.size}
          color={neuron.color}
          speed={neuron.speed}
        />
      ))}
      
      {/* Connexions entre neurones */}
      {neuralConnections.map((connection, i) => (
        <NeuralConnection
          key={`connection-${i}`}
          startNeuron={connection.startNeuron}
          endNeuron={connection.endNeuron}
          color={connection.color}
          pulseSpeed={connection.pulseSpeed}
        />
      ))}
    </group>
  );
};

// Composant principal
const NeuralBackground = () => {
  const { isDarkMode } = useContext(ThemeContext);
  
  return (
    <BackgroundContainer isDark={isDarkMode}>
      <Canvas
        camera={{ position: [0, 0, 30], fov: 80 }} // FOV élargi pour voir plus
        gl={{ antialias: true }}
        dpr={[1, 2]}
      >
        <StarrySky isDarkMode={isDarkMode} />
        <EffectComposer>
          <Bloom
            intensity={1.2} // Intensité de brillance augmentée pour plus d'effet
            luminanceThreshold={0.05} // Seuil abaissé pour que plus d'éléments brillent
            luminanceSmoothing={0.9}
          />
        </EffectComposer>
      </Canvas>
    </BackgroundContainer>
  );
};

export default NeuralBackground;